import { apiSlice } from './apiSlice';

export const planApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The signed-in walker's Plans, as { upcoming, past, cancelled, missed }:
    // Upcoming soonest first, the others most recent first.
    getPlans: builder.query({
      query: () => ({
        url: '/plans/',
        method: 'GET',
      }),
      providesTags: ['Plan'],
    }),
    // The walker's Planned Plans for today on the Trails a QR Code lists,
    // soonest first: what scanning it offers to start.
    getPlansForQrCode: builder.query({
      query: (qrCode) => ({
        url: `/plans/qr/${encodeURIComponent(qrCode)}/`,
        method: 'GET',
      }),
      providesTags: ['Plan'],
    }),
    // One of the walker's Plans, with its Plan Guests, notes and Gear List.
    getPlan: builder.query({
      query: (planId) => ({
        url: `/plans/${planId}/`,
        method: 'GET',
      }),
      providesTags: ['Plan'],
    }),
    // status is 'TAKING', 'NOT_TAKING' or 'UNANSWERED'.
    markGearItem: builder.mutation({
      query: ({ planId, gearItemId, status }) => ({
        url: `/plans/${planId}/gear/${gearItemId}/`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Plan'],
    }),
    // Marks every Essential item on the Plan with the same status at once.
    markEssentialGearItems: builder.mutation({
      query: ({ planId, status }) => ({
        url: `/plans/${planId}/gear/essentials/`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Plan'],
    }),
    addGearItem: builder.mutation({
      query: ({ planId, name }) => ({
        url: `/plans/${planId}/gear/`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Plan'],
    }),
    // notes are the walker's own text; blank clears them.
    writePlanNotes: builder.mutation({
      query: ({ planId, notes }) => ({
        url: `/plans/${planId}/`,
        method: 'PATCH',
        body: { notes },
      }),
      invalidatesTags: ['Plan'],
    }),
    // changes are any of trail_option_id (on the Plan's Trail),
    // emergency_contact_id, planned_date, planned_start_time and notes; only
    // those given change. Only a Planned Plan can be edited.
    editPlan: builder.mutation({
      query: ({ planId, changes }) => ({
        url: `/plans/${planId}/`,
        method: 'PATCH',
        body: changes,
      }),
      invalidatesTags: ['Plan'],
    }),
    cancelPlan: builder.mutation({
      query: (planId) => ({
        url: `/plans/${planId}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: ['Plan'],
    }),
    // guest is { name, phone_number }; phone_number is optional, with its
    // country code (E.164).
    addPlanGuest: builder.mutation({
      query: ({ planId, guest }) => ({
        url: `/plans/${planId}/guests/`,
        method: 'POST',
        body: guest,
      }),
      invalidatesTags: ['Plan'],
    }),
    editPlanGuest: builder.mutation({
      query: ({ planId, guestId, guest }) => ({
        url: `/plans/${planId}/guests/${guestId}/`,
        method: 'PATCH',
        body: guest,
      }),
      invalidatesTags: ['Plan'],
    }),
    removePlanGuest: builder.mutation({
      query: ({ planId, guestId }) => ({
        url: `/plans/${planId}/guests/${guestId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Plan'],
    }),
    // planned_date ('YYYY-MM-DD') and planned_start_time ('HH:MM') are
    // Trail-local.
    createPlan: builder.mutation({
      query: (plan) => ({
        url: '/plans/',
        method: 'POST',
        body: plan,
      }),
      invalidatesTags: ['Plan'],
    }),
    // Starting a Plan creates the walker's Activity, so their profile's
    // active_checkin changes too. qrCode is the permanent code of the QR Code
    // the walker scanned to start it, if any, recorded on the check-in.
    startPlan: builder.mutation({
      query: ({ planId, qrCode }) => ({
        url: `/plans/${planId}/start/`,
        method: 'POST',
        body: qrCode ? { qr_code: qrCode } : {},
      }),
      invalidatesTags: ['Plan', 'User'],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetPlanQuery,
  useGetPlansForQrCodeQuery,
  useCreatePlanMutation,
  useStartPlanMutation,
  useEditPlanMutation,
  useCancelPlanMutation,
  useMarkGearItemMutation,
  useMarkEssentialGearItemsMutation,
  useAddGearItemMutation,
  useWritePlanNotesMutation,
  useAddPlanGuestMutation,
  useEditPlanGuestMutation,
  useRemovePlanGuestMutation,
} = planApiSlice;
